import { Box, CircularProgress, Stack } from "@mui/material";
import { useStateWithSearchParams, useDebounceValue } from "../hooks";
import { useParams } from "react-router-dom";
import { useGetAccountsFiltersQuery, useGetEngagementViewQuery } from "../api";
import { Order, TrendShift } from "../types";
import { DropdownWithAccordion } from "../components/ui/dropdown";
import { ResetAllFiltersButton } from "../components/features/ResetAllFiltersButton";
import { prepareFilters } from "../utils/prepareFilters";
import { EngagementScoreTable } from "../components/accounts";
import { TopTabs } from "../components/ui/TopTabs";
import { CustomCalendar } from "../components/ui/calendar";
import { InputWithQuery } from "../components/ui/InputWithQuery";
import { NoAccountData } from "../components/accounts/NoAccountData";
import { QUERY_SEPARATOR } from "../constant";
import { GrowingDropdown } from "../components/ui";

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  page?: string;
  per_page?: string;
  selected?: string;
  limit?: string;
  date?: string;
  trend?: string;
};

const AccountsEngagementPage = () => {
  const { campaign_id } = useParams();

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    industries: undefined,
    regions: undefined,
    sizes: undefined,
    search: undefined,
    sort: undefined,
    page: undefined,
    per_page: undefined,
    selected: undefined,
    limit: "10",
    date: undefined,
    trend: undefined,
  });

  const searchValueDebounce = useDebounceValue(searchDict.search || "");

  const filters = useGetAccountsFiltersQuery(
    {
      campaign_id: campaign_id!,
    },
    { skip: !campaign_id },
  );

  const { data = [], isFetching } = useGetEngagementViewQuery(
    {
      campaign_id: campaign_id!,
      limit: searchDict.limit ? Number(searchDict?.limit) : undefined,
      size: searchDict.sizes?.split(QUERY_SEPARATOR),
      search: searchValueDebounce || undefined,
      industry: searchDict.industries?.split(QUERY_SEPARATOR),
      region: searchDict.regions?.split(QUERY_SEPARATOR),
      page: searchDict.page ? Number(searchDict?.page) : undefined,
      per_page: searchDict.per_page ? Number(searchDict?.per_page) : undefined,
      sort: searchDict.sort?.split("-")[0],
      order: searchDict.sort?.split("-")[1] as Order,
      start_date: searchDict.date?.split(QUERY_SEPARATOR)[0],
      end_date: searchDict.date?.split(QUERY_SEPARATOR)[1],
      trend_shift: searchDict.trend
        ? (searchDict.trend.split(" ")[1].toLowerCase() as TrendShift)
        : undefined,
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  const isFilters =
    prepareFilters(filters.data).some(
      ({ query }) => searchDict[query as keyof SearchParams],
    ) ||
    !!searchDict.trend ||
    !!searchDict.date;

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <TopTabs />
        <Stack direction="row" gap="8px">
          <InputWithQuery />
          <GrowingDropdown />
          {prepareFilters(filters.data).length > 0 && (
            <DropdownWithAccordion
              isActive={prepareFilters(filters.data).some(
                ({ query }) => searchDict[query as keyof SearchParams],
              )}
              data={prepareFilters(filters.data)}
              selected={prepareFilters(filters.data).reduce(
                (acc, { query }) =>
                  acc +
                  (searchDict[query as keyof SearchParams]?.split(
                    QUERY_SEPARATOR,
                  )?.length || 0),
                0,
              )}
            />
          )}
          <CustomCalendar date={searchDict.date} />
          {isFilters && <ResetAllFiltersButton />}
        </Stack>
      </Stack>
      {isFetching ? (
        <Box
          display="flex"
          height="25vh"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={80} sx={{ color: "#FE8836" }} />
        </Box>
      ) : (
        <>
          {data.length === 0 &&
          (isFilters || searchValueDebounce || !!searchDict.selected) ? (
            <NoAccountData />
          ) : (
            <EngagementScoreTable data={data || []} />
          )}
        </>
      )}
    </>
  );
};

export default AccountsEngagementPage;
